import { Field, Form, withFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Col, Container, Row } from 'reactstrap';
import * as Yup from 'yup';
import { useVSPixelScript } from '../../assets/hooks';
import AirConditioningSystemEnum from '../../assets/js/AirConditioningSystemEnum';
import WaterHeaterTypeEnum from '../../assets/js/WaterHeaterTypeEnum';
import BackBtn from '../../components/UI/BackButton/BackButton';
import Button from '../../components/UI/Button/CustomButton';
import CurrencyFormat from '../../components/UI/Form/NumberFormat/CurrencyFormat';
import {
  REQUESTEDLOANAMOUNT_TOOLTIP_TEXT,
  TOTALPRICE_TOOLTIP_TEXT,
} from '../../constants/tooltipTexts';
import { reactGAEvent, reactGAPageview } from '../../helper';
import { changeMicrofInfoValue, changePage, submitMindfire } from '../../redux/actions/index';
import { MicrofFlowPageNumber } from './MicrofFlow';

const LeaseAmount = props => {
  const {
    email,
    isReview,
    totalPrice,
    whTotalPrice,
    requestedLoanAmount,
    whRequestedLoanAmount,
    dispatchChangeInfoValue,
    dispatchChangePage,
    children,
    dispatchSubmitMindfire,
    dealerUserId,
    did,
    state,
    consumerId,
    dealerId,
    systemType,
    waterHeater,
  } = props;

  useVSPixelScript();

  useEffect(() => {
    reactGAPageview({
      path: '/ca_hvacinfo',
      trackerNames: [],
      title: 'ca_hvacinfo',
      did,
      dealerUserId,
    });
    // eslint-disable-next-line
  }, []);

  const isWaterHeater = systemType === AirConditioningSystemEnum.WATER_HEATER.id;

  const isHvacPlusWaterHeater =
    systemType === AirConditioningSystemEnum.AIR_CONDITION_OR_HEATING_SYSTEM_PLUS_WATER_HEATER.id;

  const isTank = waterHeater === WaterHeaterTypeEnum.TANK_WATER_HEATER.value;
  const isTankless = waterHeater === WaterHeaterTypeEnum.TANKLESS_WATER_HEATER.value;

  const whReqAmountMin = isTank ? 1000 : isTankless ? 2500 : 0;
  const whReqAmountMax = isTank ? 4000 : isTankless ? 8500 : 0;

  const states3000 = ['CT', 'IA', 'ME', 'OH', 'PA', 'VT', 'CA', 'NY', 'WV'];
  const statesDownPayment = ['CA', 'IN', 'WV'];

  const LeaseAmountForm = () => {
    return (
      <Form noValidate>
        {!isWaterHeater && (
          <>
            <Field
              component={CurrencyFormat}
              label="HVAC Total Job Price"
              name="totalPrice"
              id="totalPrice"
              tooltipText={TOTALPRICE_TOOLTIP_TEXT}
              placeholder="$0.00"
              type="text"
            />
            <Field
              component={CurrencyFormat}
              label="HVAC Requested Lease Amount"
              name="requestedLoanAmount"
              id="requestedLoanAmount"
              tooltipText={REQUESTEDLOANAMOUNT_TOOLTIP_TEXT}
              placeholder="$0.00"
              type="text"
            />
          </>
        )}

        {isWaterHeater || isHvacPlusWaterHeater ? (
          <>
            <Field
              component={CurrencyFormat}
              label="Water Heater Total Job Price"
              name="whTotalPrice"
              id="whTotalPrice"
              tooltipText={TOTALPRICE_TOOLTIP_TEXT}
              placeholder="$0.00"
              type="text"
            />
            <Field
              component={CurrencyFormat}
              label="Water Heater Requested Lease Amount"
              name="whRequestedLoanAmount"
              id="whRequestedLoanAmount"
              tooltipText={REQUESTEDLOANAMOUNT_TOOLTIP_TEXT}
              placeholder="$0.00"
              type="text"
            />
          </>
        ) : null}

        <Button type="submit" title={isReview ? 'Review' : 'Continue'} />
      </Form>
    );
  };

  const LeaseAmountFormFormik = withFormik({
    mapPropsToValues: () => ({
      totalPrice: totalPrice || '',
      requestedLoanAmount: requestedLoanAmount || '',
      whTotalPrice: whTotalPrice || '',
      whRequestedLoanAmount: whRequestedLoanAmount || '',
    }),

    validationSchema: Yup.object({
      totalPrice: !isWaterHeater
        ? Yup.number()
            .required('Please enter the Total price.')
            .test('min3000', 'Total price should be greater than $3000.', val => {
              return states3000.includes(state) ? val >= 3000 : true;
            })
            .test('min2500', 'Total price should be greater than $2,500.', val => {
              return !states3000.includes(state) ? val >= 2500 : true;
            })
            .test('max15000', 'Amount should be lower than 15,000.', val => {
              return dealerId && (!consumerId || did) ? true : val <= 15000;
            })
        : Yup.number(),

      whTotalPrice:
        isWaterHeater || isHvacPlusWaterHeater
          ? Yup.number()
              .required('Please enter the Total price.')
              .test('min2500', `Total price should be greater than $${whReqAmountMin}.`, val => {
                return states3000.includes(state) ? val >= whReqAmountMin : true;
              })
              .test('min1000', `Total price should be greater than $${whReqAmountMin}.`, val => {
                return !states3000.includes(state) ? val >= whReqAmountMin : true;
              })
              .test('max15000', 'Amount should be lower than 15,000.', val => {
                return dealerId && (!consumerId || did) ? true : val <= 15000;
              })
          : Yup.number(),

      requestedLoanAmount: !isWaterHeater
        ? dealerId && (!consumerId || did)
          ? Yup.number()
              .required('Please enter an amount.')
              .test('min3000', 'Amount should be greater than $3000.', val => {
                return states3000.includes(state) ? val >= 3000 : true;
              })
              .test('min2500', 'Amount should be greater than $2,500.', val => {
                return !states3000.includes(state) ? val >= 2500 : true;
              })
              .lessThan(15000.01, `Amount should be lower than 15,000.`)
              .test({
                name: 'downPaymentProhibited',
                exclusive: false,
                message: 'A capitalized cost reduction (down payment) is prohibited in your state.',
                test: function(value) {
                  return statesDownPayment.includes(state)
                    ? value >= this.resolve(Yup.ref('totalPrice'))
                    : true;
                },
              })
              .test({
                name: 'lowerThan',
                exclusive: false,
                message: 'Amount cannot be higher than Total price.',
                test: function(value) {
                  return value <= this.resolve(Yup.ref('totalPrice'));
                },
              })
              .test({
                name: 'lowerThan',
                exclusive: false,
                message: 'Amount must be greater than 60% of the Total price.',
                test: function(value) {
                  return value >= this.resolve(Yup.ref('totalPrice')) * 0.6;
                },
              })
          : Yup.number()
        : Yup.number(),

      whRequestedLoanAmount:
        isWaterHeater || isHvacPlusWaterHeater
          ? dealerId && (!consumerId || did)
            ? Yup.number()
                .required('Please enter an amount.')
                .test('min2500', `Total price should be greater than $${whReqAmountMin}.`, val => {
                  return states3000.includes(state) ? val >= whReqAmountMin : true;
                })
                .test('min1000', `Total price should be greater than $${whReqAmountMin}.`, val => {
                  return !states3000.includes(state) ? val >= whReqAmountMin : true;
                })
                .lessThan(
                  whReqAmountMax + 0.01,
                  `Amount should be lower or equal $${whReqAmountMax}.`,
                )
                .test({
                  name: 'downPaymentProhibited',
                  exclusive: false,
                  message:
                    'A capitalized cost reduction (down payment) is prohibited in your state.',
                  test: function(value) {
                    return statesDownPayment.includes(state)
                      ? value >= this.resolve(Yup.ref('whTotalPrice'))
                      : true;
                  },
                })
                .test({
                  name: 'lowerThan',
                  exclusive: false,
                  message: 'Amount cannot be higher than Total price.',
                  test: function(value) {
                    return value <= this.resolve(Yup.ref('whTotalPrice'));
                  },
                })
                .test({
                  name: 'lowerThan',
                  exclusive: false,
                  message: 'Amount must be greater than 60% of the Total price.',
                  test: function(value) {
                    return value >= this.resolve(Yup.ref('whTotalPrice')) * 0.6;
                  },
                })
            : Yup.number()
          : Yup.number(),
    }),

    handleSubmit: values => {
      reactGAEvent({
        category: 'Button',
        action: 'Click',
        label: 'Continue',
        did,
        dealerUserId,
      });
      dispatchChangeInfoValue('totalPrice', values.totalPrice);
      dispatchChangeInfoValue(
        'requestedLoanAmount',
        dealerId && (!consumerId || did) ? values.requestedLoanAmount : values.totalPrice,
      );
      dispatchChangeInfoValue('whTotalPrice', values.whTotalPrice);
      dispatchChangeInfoValue(
        'whRequestedLoanAmount',
        dealerId && (!consumerId || did) ? values.whRequestedLoanAmount : values.whTotalPrice,
      );
      dispatchChangePage(
        isReview ? MicrofFlowPageNumber.Review : MicrofFlowPageNumber.AppIncomeInfo,
      );
      dispatchChangeInfoValue('isReview', null);
      dispatchSubmitMindfire({
        email: email,
        dealerUserId: dealerUserId,
        lastPage: '4076',
        did: did,
      });
    },
  })(LeaseAmountForm);
  return (
    <Container>
      <Row>
        <BackBtn
          changePage={dispatchChangePage}
          page={
            isWaterHeater || isHvacPlusWaterHeater
              ? MicrofFlowPageNumber.WaterHeater
              : MicrofFlowPageNumber.HvacHeatType
          }
        />
        <Col sm="5">{children}</Col>
        <Col sm="6">
          <LeaseAmountFormFormik />
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = state => ({
  consumerId: state.auth.consumerId,
  dealerId: state.auth.dealerId,
  totalPrice: state.microfInformation.microfOnly
    ? state.microfInformation.borrowedAmount ||
      state.microfInformation.totalPrice ||
      (!state.auth.dealerId && 6000) ||
      0
    : state.microfInformation.totalPrice || 0,
  requestedLoanAmount:
    state.microfInformation.requestedLoanAmount ||
    state.powerPayInformation.borrowedAmount ||
    state.wellsFargoInformation.borrowedAmount ||
    state.preQualificationInformation.borrowedAmount ||
    0,
  whTotalPrice: state.microfInformation.whTotalPrice,
  whRequestedLoanAmount: state.microfInformation.whRequestedLoanAmount,
  isReview: state.microfInformation.isReview,
  email: state.preQualificationInformation.email,
  state: state.preQualificationInformation.state,
  dealerUserId: state.auth.dealerUserId,
  did: state.auth.did,
  systemType: state.microfInformation.systemType,
  waterHeater: state.microfInformation.waterHeater,
});

const mapDispatchToProps = dispatch => ({
  dispatchChangePage: page => dispatch(changePage(page)),
  dispatchChangeInfoValue: (key, value) => dispatch(changeMicrofInfoValue(key, value)),
  dispatchSubmitMindfire: params => dispatch(submitMindfire(params)),
});

LeaseAmount.propTypes = {
  totalPrice: PropTypes.number,
  requestedLoanAmount: PropTypes.number,
  hvacSystem: PropTypes.string,
  hvacTonnage: PropTypes.string,
  secondHvacSystem: PropTypes.string,
  secondHvacTonnage: PropTypes.string,
  isReview: PropTypes.bool,
  dispatchChangePage: PropTypes.func,
  dispatchChangeInfoValue: PropTypes.func,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(LeaseAmount);
